<script lang="ts">
	/**
	 * What an application's webhook receiver answered, for the admin delivery tables (§8.1).
	 *
	 * The tables used to show a status and an error string, and between them they cannot answer the
	 * question an operator arrives with. "It returned 500" is not a diagnosis; the stack trace in
	 * the body naming the field the receiver choked on is. And a **success** is worse served than a
	 * failure was — a receiver answering `200 {"ok":false,"reason":"unknown user"}` is counted
	 * delivered by the store, has done nothing at its end, and this is the only place that shows.
	 *
	 * Collapsed by default. A delivery table is scanned for the row that went wrong, and a body
	 * expanded on every row would bury the list it is attached to.
	 *
	 * **Null is not empty.** No recorded body means no answer was seen at all — a timeout, a
	 * refused connection, a URL the SSRF check turned down — which is a different fact from a
	 * receiver that replied with nothing, and the two are said differently.
	 */

	import { Button } from 'flowbite-svelte';
	import type { WebhookDelivery } from '../AppTypes';

	export let delivery: WebhookDelivery;

	let open: boolean = false;

	/** Whether the receiver answered at all. See the note above: null and empty are different. */
	$: answered = delivery.lastResponseBody !== null && delivery.lastResponseBody !== undefined;
	$: headers = Object.entries(delivery.lastResponseHeaders ?? {});
	/** The server caps the stored body; this is how the screen admits it did. */
	$: truncated =
		delivery.lastResponseBytes !== null &&
		answered &&
		delivery.lastResponseBytes > (delivery.lastResponseBody as string).length;
</script>

{#if answered || headers.length > 0}
	<Button size="xs" color="none" class="!p-0 text-primary-500 hover:underline" on:click={() => (open = !open)}>
		<i class="fa-solid fa-chevron-{open ? 'down' : 'right'} mr-1 text-[10px]" />
		{open ? 'Hide response' : 'Show response'}
	</Button>

	{#if open}
		<div class="widget bg-depth mt-2 space-y-2 text-xs">
			{#if delivery.lastDurationMs !== null}
				<div class="text-default">
					<i class="fa-solid fa-stopwatch mr-1" />Answered in {delivery.lastDurationMs} ms
				</div>
			{/if}

			{#if headers.length > 0}
				<div>
					<p class="card-label mb-1">Headers</p>
					<dl class="grid grid-cols-[auto_1fr] gap-x-3">
						{#each headers as [name, value] (name)}
							<dt class="font-mono text-default">{name}</dt>
							<!-- Credential-bearing headers arrive already redacted; this renders whatever
							     the server chose to keep and never decides that itself. -->
							<dd class="font-mono break-all">{value}</dd>
						{/each}
					</dl>
				</div>
			{/if}

			{#if answered}
				<div>
					<p class="card-label mb-1">
						Body
						{#if truncated}
							<span class="text-yellow-600 dark:text-yellow-400 normal-case tracking-normal">
								(truncated from {delivery.lastResponseBytes} bytes)
							</span>
						{/if}
					</p>
					{#if delivery.lastResponseBody === ''}
						<!-- Answered, with nothing. Distinct from having answered at all. -->
						<p class="text-default italic">The receiver answered with an empty body.</p>
					{:else}
						<pre class="whitespace-pre-wrap break-all font-mono">{delivery.lastResponseBody}</pre>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
{:else if delivery.lastAttemptAt !== null}
	<!--
		Attempted, and nothing came back. A timeout, a refused connection or a URL the SSRF check
		turned down all land here, and saying "no response" is the honest reading — an empty body
		would claim the receiver replied.
	-->
	<span class="text-default text-xs">No response received</span>
{/if}
